import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLightbulbOn } from '@fortawesome/pro-light-svg-icons/faLightbulbOn';
import { faRedo } from '@fortawesome/pro-solid-svg-icons/faRedo';

import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import { Input } from 'ui-components/components/input';
import { Button } from 'ui-components/components/button';
import { Checkbox } from 'ui-components/components/checkbox/';
import { RadioCheckList } from 'ui-components/components/radio-check-list';
import { RadioCheckListItem } from 'ui-components/components/radio-check-list/radio-check-list-item';

import { useStoreUpdateContext } from '../context';
import {
  STORE_REFRESH_STRATEGY_BACKGROUND,
  STORE_REFRESH_STRATEGY_AUTOMATIC,
  STORE_REFRESH_STRATEGY_MANUAL,
  IMPORT_MODES,
} from '../store-refresh-strategy-constants';
import {
  RESIDENTIAL_INDICATOR_DEFAULT_BEHAVIOR,
  RESIDENTIAL_INDICATOR_MARKETPLACE_FALLBACK_BEHAVIOR,
  RESIDENTIAL_INDICATOR,
  COMMERCIAL_INDICATOR,
  MARKETPLACE_INDICATOR,
} from './constants';
import { MarketplaceIds } from '../../../../../enums/marketplace-ids';
import GetMarketplaceDisplayName from '../../../../../components/data-driven-logo/get-marketplace-display-name';
import MarketplaceSettingsModal from './marketplace-settings-modal';
import { useMarketplaceApiCodeFromStoreGuid } from '../../../../../hooks/use-marketplace-api-code-from-store-guid';
import { useAddressValidatorConfig } from '../../../../../contexts/country-config-context/use-address-validator-config';
import { useEnableBackgroundImport } from '../../../../../feature-flags/hooks/use-enable-background-import';
import { BetaTag } from 'ui-components/components/beta-tag';
import { FlexRow } from 'ui-components/components/flex';

import messages from '../messages';
import style from '../style.module.less';

const GeneralSettingTab = ({ storeSettings, marketplaceId, storeGuid, hasMarketplaceSettings = false, refreshStore }) => {
  const intl = useIntl();
  const { updateStoreSetting, updateTrigger } = useStoreUpdateContext();
  const GROUP_NAME = 'settings';

  const [marketplaceSettingsOpen, setMarketplaceSettingsOpen] = useState(false);
  const updateName = (value) => updateStoreSetting(GROUP_NAME, 'storeName', value);
  const updateActive = (value) => updateStoreSetting(GROUP_NAME, 'active', value);
  const updateAutoAddyValy = (value) => updateStoreSetting(GROUP_NAME, 'bypassAddressSanitation', !value);
  const updateAutoIntlAddyValy = (value) => updateStoreSetting(GROUP_NAME, 'bypassInternationalAddressSanitation', !value);
  const allowAddressValidation = useAddressValidatorConfig();
  const isBackgroundImportEnabled = useEnableBackgroundImport();

  // Compute import mode from settings
  const getImportMode = () => {
    if (isBackgroundImportEnabled && storeSettings.storeRefreshStrategy === STORE_REFRESH_STRATEGY_BACKGROUND)
      return IMPORT_MODES.Background;
    if (storeSettings.autoRefresh) return IMPORT_MODES.Automatic;
    return IMPORT_MODES.Manual;
  };

  const updateImportMode = (mode) => {
    if (mode === IMPORT_MODES.Background) {
      updateStoreSetting(GROUP_NAME, 'autoRefresh', false);
      updateStoreSetting(GROUP_NAME, 'storeRefreshStrategy', STORE_REFRESH_STRATEGY_BACKGROUND);
    } else if (mode === IMPORT_MODES.Automatic) {
      updateStoreSetting(GROUP_NAME, 'autoRefresh', true);
      updateStoreSetting(GROUP_NAME, 'storeRefreshStrategy', STORE_REFRESH_STRATEGY_AUTOMATIC);
    } else {
      updateStoreSetting(GROUP_NAME, 'autoRefresh', false);
      updateStoreSetting(GROUP_NAME, 'storeRefreshStrategy', STORE_REFRESH_STRATEGY_MANUAL);
    }
  };

  const isShopify = marketplaceId === MarketplaceIds.Shopify;

  const shouldShowMarketplaceSettings = hasMarketplaceSettings;
  const marketplaceNameTranslation = GetMarketplaceDisplayName(marketplaceId);
  const marketplaceName = intl.formatMessage(marketplaceNameTranslation, marketplaceNameTranslation.values);

  const closeSettingsModal = useCallback(() => setMarketplaceSettingsOpen(false), [setMarketplaceSettingsOpen]);
  const onMarketplaceSettingsSaved = useCallback(() => {
    setMarketplaceSettingsOpen(false);
    refreshStore();
  }, [setMarketplaceSettingsOpen, refreshStore]);

  const marketplaceApiCode = useMarketplaceApiCodeFromStoreGuid(storeGuid);

  return (
    <div className={style['tab-content']}>
      <div className={style['tab-title']}>
        <FormattedMessage {...messages.generalTabTitle} />
      </div>

      <div className={style.container}>
        <label>
          <FormattedMessage {...messages.storeName} />
        </label>
        <div className={classnames(style['flex-input'])}>
          <Input
            className={style['store-name']}
            onChange={updateName}
            placeholder={intl.formatMessage(messages.storeName)}
            value={storeSettings.storeName}
          />
        </div>
      </div>

      <div className={style.container}>
        <label>
          <FormattedMessage {...messages.status} />
        </label>
        <div className={style['flex-input']}>
          <RadioCheckList className={style['store-status-options-list']}>
            <RadioCheckListItem
              className={style['store-status-radio-checklist-item']}
              isChecked={storeSettings.active}
              onChecked={() => updateActive(true)}
            >
              <FormattedMessage {...messages.activeLabel} />
            </RadioCheckListItem>
            <RadioCheckListItem
              className={style['store-status-radio-checklist-item']}
              isChecked={!storeSettings.active}
              onChecked={() => updateActive(false)}
            >
              <FormattedMessage {...messages.inactiveLabel} />
            </RadioCheckListItem>
          </RadioCheckList>
        </div>
        <div className={style.note}>
          <FormattedMessage {...messages.storeStatusNote} />
        </div>
      </div>

      <div className={style['import-frequency-container']}>
        <div className={style.container}>
          <label>
            <FormattedMessage {...messages.importFrequency} />
          </label>
          <div className={style['flex-input']}>
            <RadioCheckList className={style['import-frequency-options-list']}>
              <RadioCheckListItem
                className={style['radio-checklist-item']}
                id='manual-import'
                isChecked={getImportMode() === IMPORT_MODES.Manual}
                onChecked={() => {
                  updateImportMode(IMPORT_MODES.Manual);
                }}
              >
                <FormattedMessage {...messages.manualImport} />
                <div className={style.note}>
                  <FormattedMessage {...messages.manualImportNote} />
                </div>
              </RadioCheckListItem>
              <RadioCheckListItem
                className={style['radio-checklist-item']}
                id='periodic-import'
                isChecked={getImportMode() === IMPORT_MODES.Automatic}
                onChecked={() => {
                  updateImportMode(IMPORT_MODES.Automatic);
                }}
              >
                <FormattedMessage {...messages.automaticImport} />
                <div className={style.note}>
                  {storeSettings.automaticImportIsInstantOrderSync ? (
                    <FormattedMessage {...messages.automaticImportNoteForIOS} />
                  ) : (
                    <FormattedMessage {...messages.automaticImportNote} />
                  )}
                </div>
              </RadioCheckListItem>
              {isShopify && isBackgroundImportEnabled && (
                <RadioCheckListItem
                  className={style['radio-checklist-item']}
                  id='background-import'
                  isChecked={getImportMode() === IMPORT_MODES.Background}
                  onChecked={() => {
                    updateImportMode(IMPORT_MODES.Background);
                  }}
                >
                  <FlexRow>
                    <FormattedMessage {...messages.backgroundImport} />
                    <BetaTag />
                  </FlexRow>
                  <div className={style.note}>
                    <FormattedMessage {...messages.backgroundImportNote} />
                  </div>
                </RadioCheckListItem>
              )}
            </RadioCheckList>
          </div>
        </div>
        <div className={style['insight-container']}>
          <div className={style.title}>
            <div className={style.icon}>
              <FontAwesomeIcon className={classnames(style['light-bulb-icon'], 'fa-regular')} icon={faLightbulbOn} />
            </div>
            <div className={style.text}>
              <FormattedMessage {...messages.insightImportFrequencyTitle} />
            </div>
          </div>
          <div className={style.content}>
            <FormattedMessage
              {...messages.insightImportFrequencyContent}
              values={{
                icon: <FontAwesomeIcon className={style['redo-icon']} icon={faRedo} />,
              }}
            />
          </div>
        </div>
      </div>

      <div className={style.container}>
        <label>
          <FormattedMessage {...messages.standardizeAddress} />
        </label>
        <div className={style['flex-input']}>
          <Checkbox
            checked={!storeSettings.bypassAddressSanitation}
            className={style.checkbox}
            label={<FormattedMessage {...messages.standardizeUSAddressLabel} />}
            onChange={updateAutoAddyValy}
          />
        </div>
        {allowAddressValidation && (
          <div className={style['flex-input']}>
            <Checkbox
              checked={!storeSettings.bypassInternationalAddressSanitation}
              className={style.checkbox}
              label={<FormattedMessage {...messages.standardizeIntlAddressLabel} />}
              onChange={updateAutoIntlAddyValy}
            />
            <div className={classnames(style.note, style['checkbox-note'])}>
              <FormattedMessage {...messages.autoCorrectAddressesIntlMessage} />
            </div>
          </div>
        )}
      </div>

      <div className={style.container}>
        <label>
          <FormattedMessage {...messages.residentialCommercialIndicator} />
        </label>
        <div className={style.note}>
          <FormattedMessage {...messages.residentialCommercialIndicatorDescription} />
        </div>
        <div className={style['flex-input']}>
          <RadioCheckList className={style['import-frequency-options-list']}>
            <RadioCheckListItem
              className={style['radio-checklist-item']}
              id='default-commercial'
              isChecked={
                !storeSettings.storeSettings?.some(
                  (setting) =>
                    setting.name === RESIDENTIAL_INDICATOR_DEFAULT_BEHAVIOR &&
                    (setting.value === RESIDENTIAL_INDICATOR || setting.value === MARKETPLACE_INDICATOR),
                )
              }
              onChecked={() => updateTrigger(RESIDENTIAL_INDICATOR_DEFAULT_BEHAVIOR, COMMERCIAL_INDICATOR)}
            >
              <FormattedMessage {...messages.defaultToCommercial} />
            </RadioCheckListItem>
            <RadioCheckListItem
              className={style['radio-checklist-item']}
              id='default-residential'
              isChecked={storeSettings.storeSettings?.some(
                (setting) => setting.name === RESIDENTIAL_INDICATOR_DEFAULT_BEHAVIOR && setting.value === RESIDENTIAL_INDICATOR,
              )}
              onChecked={() => updateTrigger(RESIDENTIAL_INDICATOR_DEFAULT_BEHAVIOR, RESIDENTIAL_INDICATOR)}
            >
              <FormattedMessage {...messages.defaultToResidential} />
            </RadioCheckListItem>
            <RadioCheckListItem
              className={style['radio-checklist-item']}
              id='default-marketplace'
              isChecked={storeSettings.storeSettings?.some(
                (setting) => setting.name === RESIDENTIAL_INDICATOR_DEFAULT_BEHAVIOR && setting.value === MARKETPLACE_INDICATOR,
              )}
              onChecked={() => updateTrigger(RESIDENTIAL_INDICATOR_DEFAULT_BEHAVIOR, MARKETPLACE_INDICATOR)}
            >
              <FormattedMessage {...messages.defaultToMarketplace} />
            </RadioCheckListItem>
          </RadioCheckList>
        </div>
        {storeSettings.storeSettings?.some(
          (setting) => setting.name === RESIDENTIAL_INDICATOR_DEFAULT_BEHAVIOR && setting.value === MARKETPLACE_INDICATOR,
        ) && (
          <div className={style.conditions}>
            <RadioCheckList className={style['marketplace-fallback-list']}>
              <RadioCheckListItem
                className={style['radio-checklist-item']}
                id='marketplace-fallback-commercial'
                isChecked={
                  !storeSettings.storeSettings?.some(
                    (setting) =>
                      setting.name === RESIDENTIAL_INDICATOR_MARKETPLACE_FALLBACK_BEHAVIOR && setting.value === RESIDENTIAL_INDICATOR,
                  )
                }
                onChecked={() => updateTrigger(RESIDENTIAL_INDICATOR_MARKETPLACE_FALLBACK_BEHAVIOR, COMMERCIAL_INDICATOR)}
              >
                <FormattedMessage {...messages.marketplaceFallbackToCommercial} />
              </RadioCheckListItem>
              <RadioCheckListItem
                className={style['radio-checklist-item']}
                id='marketplace-fallback-residential'
                isChecked={storeSettings.storeSettings?.some(
                  (setting) =>
                    setting.name === RESIDENTIAL_INDICATOR_MARKETPLACE_FALLBACK_BEHAVIOR && setting.value === RESIDENTIAL_INDICATOR,
                )}
                onChecked={() => updateTrigger(RESIDENTIAL_INDICATOR_MARKETPLACE_FALLBACK_BEHAVIOR, RESIDENTIAL_INDICATOR)}
              >
                <FormattedMessage {...messages.marketplaceFallbackToResidential} />
              </RadioCheckListItem>
            </RadioCheckList>
          </div>
        )}
      </div>
      {shouldShowMarketplaceSettings && (
        <div className={style['show-marketplace-settings']}>
          <h3>
            <FormattedMessage
              {...messages.showMarketplaceSettingsHeader}
              values={{
                marketplaceName,
              }}
            />
          </h3>
          <Button onClick={() => setMarketplaceSettingsOpen(true)} theme='secondary'>
            <FormattedMessage
              {...messages.showMarketplaceSettingsButtonText}
              values={{
                marketplaceName,
              }}
            />
          </Button>
        </div>
      )}
      {marketplaceSettingsOpen && (
        <MarketplaceSettingsModal
          marketplaceApiCode={marketplaceApiCode}
          onClose={closeSettingsModal}
          onSave={onMarketplaceSettingsSaved}
          storeGuid={storeGuid}
        />
      )}
    </div>
  );
};

GeneralSettingTab.propTypes = {
  hasErrors: PropTypes.bool,
  handleSave: PropTypes.func.isRequired,
  storeSettings: PropTypes.object.isRequired,
  marketplaceId: PropTypes.number.isRequired,
  storeGuid: PropTypes.string.isRequired,
  hasMarketplaceSettings: PropTypes.bool,
  refreshStore: PropTypes.func.isRequired,
};

export default GeneralSettingTab;
